import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../hooks/useAuth';
import { useSupabaseQuery } from '../../hooks/useSupabaseQuery';
import { PageHeader } from '../../components/ui/PageHeader';
import { EmptyState } from '../../components/ui/EmptyState';
import { isStaff } from '../../lib/roles';
import { estadoCitaBadge } from '../../types/domain';
import type { CitaConRelaciones } from '../../types/domain';
import type { EstadoCita, ProfesionalRow, ServicioRow } from '../../types/database.types';
import { NuevaCitaForm } from './NuevaCitaForm';

function formatearFecha(d: Date) {
  return d.toISOString().slice(0, 10);
}

function sumarDias(fecha: string, dias: number) {
  const d = new Date(`${fecha}T00:00:00`);
  d.setDate(d.getDate() + dias);
  return formatearFecha(d);
}

/** Lunes de la semana ISO que contiene `fecha` ('YYYY-MM-DD'). */
function inicioSemana(fecha: string): string {
  const d = new Date(`${fecha}T00:00:00`);
  const dow = d.getDay(); // 0=domingo … 6=sábado
  const diasDesdeLunes = dow === 0 ? 6 : dow - 1;
  d.setDate(d.getDate() - diasDesdeLunes);
  return formatearFecha(d);
}

const ETIQUETA_DIA = new Intl.DateTimeFormat('es-CO', { weekday: 'long', day: 'numeric', month: 'short' });

/**
 * Agenda (DAF v2, sección 10). Vista día y vista semana sobre la misma
 * consulta base, con filtro por profesional y servicio para staff.
 *
 * Para un usuario con rol `profesional`, esta pantalla no filtra nada
 * "a propósito" en el frontend por su propio id — la política RLS
 * `citas_profesional_select` ya devuelve solo sus propias citas, así que
 * mostrarle un filtro de profesional aquí no tendría sentido (por eso se
 * oculta para ese rol).
 *
 * Crear una cita manualmente (regla de negocio 14) ya no es exclusivo de
 * staff: desde schema_profesional_ventas_agenda.sql (pedido explícito
 * del usuario), una profesional también puede agendar -- pero solo
 * asignándose a sí misma (RLS `citas_profesional_insert`), nunca a otra
 * profesional -- ver `NuevaCitaForm.tsx` y su prop `profesionalIdFijo`.
 * Cambiar el estado de una cita (confirmar, marcar atendida, cancelar,
 * no asistió) es staff para cualquier cita del salón, o la propia
 * profesional para sus propias citas (regla: "marcar citas como
 * atendidas", DAF sección 5.3) — en ambos casos la barrera real es RLS
 * (`citas_staff_all` / `citas_profesional_update`), esto solo decide qué
 * botones mostrar.
 *
 * Flujo conectado (pedido explícito del usuario): cuando una PROFESIONAL
 * marca su propia cita como "Atendida", en vez de quedarse en la agenda
 * se le lleva directo a /admin/ventas/nueva con la clienta, el servicio
 * y ella misma ya precargados (ver VentaForm.tsx, lectura de
 * searchParams) -- "apenas ponga la clienta como atendida y haga la
 * venta". Para staff el comportamiento no cambia (se queda en la
 * agenda) -- ya tienen su propio flujo de ventas independiente y
 * probado, y no se quiso alterarlo sin que lo pidieran.
 */
export function Agenda() {
  const { perfil } = useAuth();
  const staff = isStaff(perfil?.rol);
  const esProfesional = perfil?.rol === 'profesional';
  const navigate = useNavigate();

  const [vista, setVista] = useState<'dia' | 'semana'>('dia');
  const [fecha, setFecha] = useState(() => formatearFecha(new Date()));
  const [profesionalId, setProfesionalId] = useState('');
  const [servicioId, setServicioId] = useState('');
  const [mostrarForm, setMostrarForm] = useState(false);

  const [profesionales, setProfesionales] = useState<ProfesionalRow[]>([]);
  const [servicios, setServicios] = useState<ServicioRow[]>([]);

  useEffect(() => {
    if (!staff || !perfil) return;
    supabase
      .from('profesionales')
      .select('*')
      .eq('salon_id', perfil.salon_id)
      .eq('estado', 'activo')
      .order('nombre')
      .then(({ data }) => setProfesionales(data ?? []));
    supabase
      .from('servicios')
      .select('*')
      .eq('salon_id', perfil.salon_id)
      .eq('estado', 'activo')
      .order('nombre')
      .then(({ data }) => setServicios(data ?? []));
  }, [staff, perfil]);

  const rango = useMemo(() => {
    const inicioStr = vista === 'dia' ? fecha : inicioSemana(fecha);
    const inicio = new Date(`${inicioStr}T00:00:00`);
    const fin = new Date(inicio);
    fin.setDate(fin.getDate() + (vista === 'dia' ? 1 : 7));
    return { inicio: inicio.toISOString(), fin: fin.toISOString(), inicioStr };
  }, [fecha, vista]);

  const {
    data,
    loading,
    error,
    refetch,
  } = useSupabaseQuery<CitaConRelaciones[]>(async () => {
    let query = supabase
      .from('citas')
      .select('*, clientes ( nombre, apellido ), servicios ( nombre ), profesionales ( nombre )')
      .gte('inicio', rango.inicio)
      .lt('inicio', rango.fin)
      .order('inicio', { ascending: true });

    if (profesionalId) query = query.eq('profesional_id', profesionalId);
    if (servicioId) query = query.eq('servicio_id', servicioId);

    const { data, error } = await query;
    if (error) return { data: null, error };

    const items = (data as unknown as Array<Record<string, unknown>>).map((row) => {
      const cliente = row.clientes as { nombre: string; apellido: string | null } | null;
      return {
        ...(row as unknown as CitaConRelaciones),
        cliente_nombre: cliente ? `${cliente.nombre} ${cliente.apellido ?? ''}`.trim() : '—',
        servicio_nombre: (row.servicios as { nombre: string } | null)?.nombre ?? '—',
        profesional_nombre: (row.profesionales as { nombre: string } | null)?.nombre ?? '—',
      };
    });

    return { data: items, error: null };
  }, [rango.inicio, rango.fin, profesionalId, servicioId]);

  async function actualizarEstado(cita: CitaConRelaciones, estado: EstadoCita) {
    const { error: err } = await supabase.from('citas').update({ estado }).eq('id', cita.id);

    if (!err && estado === 'atendida' && esProfesional) {
      // Flujo conectado: la lleva directo a registrar la venta de esta
      // cita, con clienta/servicio/ella misma ya precargados.
      const params = new URLSearchParams({
        cliente: cita.cliente_id,
        cita: cita.id,
        servicio: cita.servicio_id,
        profesional: cita.profesional_id,
      });
      navigate(`/admin/ventas/nueva?${params.toString()}`);
      return;
    }

    refetch();
  }

  function alCrear() {
    setMostrarForm(false);
    refetch();
  }

  const grupos = useMemo(() => {
    if (!data) return [];
    if (vista === 'dia') return [{ fecha: rango.inicioStr, citas: data }];
    const porDia = new Map<string, CitaConRelaciones[]>();
    for (const c of data) {
      const clave = formatearFecha(new Date(c.inicio));
      const lista = porDia.get(clave) ?? [];
      lista.push(c);
      porDia.set(clave, lista);
    }
    return [...porDia.entries()].sort(([a], [b]) => a.localeCompare(b)).map(([fecha, citas]) => ({ fecha, citas }));
  }, [data, vista, rango.inicioStr]);

  return (
    <div>
      <PageHeader
        title="Agenda"
        description="Citas del salón"
        actions={
          staff || esProfesional ? (
            <button className="btn-primary" onClick={() => setMostrarForm((v) => !v)}>
              {mostrarForm ? 'Cancelar' : 'Nueva cita'}
            </button>
          ) : undefined
        }
      />

      {mostrarForm && perfil && (
        <NuevaCitaForm
          salonId={perfil.salon_id}
          usuarioId={perfil.id}
          profesionalIdFijo={esProfesional ? perfil.profesionalId : undefined}
          onCreada={alCrear}
          onCancelar={() => setMostrarForm(false)}
        />
      )}

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <div className="flex overflow-hidden rounded-md border border-onyx/15">
          <button
            className={`px-3 py-1.5 text-sm ${vista === 'dia' ? 'bg-gold/15 text-gold-deep' : 'text-onyx/60'}`}
            onClick={() => setVista('dia')}
          >
            Día
          </button>
          <button
            className={`px-3 py-1.5 text-sm ${vista === 'semana' ? 'bg-gold/15 text-gold-deep' : 'text-onyx/60'}`}
            onClick={() => setVista('semana')}
          >
            Semana
          </button>
        </div>

        <button className="btn-secondary px-3" onClick={() => setFecha((f) => sumarDias(f, vista === 'dia' ? -1 : -7))}>
          ←
        </button>
        <input type="date" className="input-field w-auto" value={fecha} onChange={(e) => setFecha(e.target.value)} />
        <button className="btn-secondary px-3" onClick={() => setFecha((f) => sumarDias(f, vista === 'dia' ? 1 : 7))}>
          →
        </button>
        <button className="btn-secondary" onClick={() => setFecha(formatearFecha(new Date()))}>
          Hoy
        </button>

        {staff && (
          <>
            <select className="input-field w-auto" value={profesionalId} onChange={(e) => setProfesionalId(e.target.value)}>
              <option value="">Todas las profesionales</option>
              {profesionales.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.nombre}
                </option>
              ))}
            </select>
            <select className="input-field w-auto" value={servicioId} onChange={(e) => setServicioId(e.target.value)}>
              <option value="">Todos los servicios</option>
              {servicios.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.nombre}
                </option>
              ))}
            </select>
          </>
        )}
      </div>

      {loading && <p className="text-sm text-onyx/50">Cargando…</p>}
      {error && <p className="text-sm text-red-600">{error}</p>}

      {data && data.length === 0 && <EmptyState title="Sin citas en este rango" />}

      {data && data.length > 0 && (
        <div className="space-y-6">
          {grupos.map((grupo) => (
            <div key={grupo.fecha}>
              {vista === 'semana' && (
                <h3 className="mb-2 text-xs font-medium uppercase tracking-wide text-onyx/40">
                  {ETIQUETA_DIA.format(new Date(`${grupo.fecha}T00:00:00`))}
                </h3>
              )}
              <div className="space-y-2">
                {grupo.citas.map((cita) => (
                  <FilaCita key={cita.id} cita={cita} staff={staff} onCambiarEstado={actualizarEstado} />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function FilaCita({
  cita,
  staff,
  onCambiarEstado,
}: {
  cita: CitaConRelaciones;
  staff: boolean;
  onCambiarEstado: (cita: CitaConRelaciones, estado: EstadoCita) => void;
}) {
  const badge = estadoCitaBadge(cita.estado);
  const hora = new Date(cita.inicio).toLocaleTimeString('es-CO', { hour: '2-digit', minute: '2-digit' });
  const activa = cita.estado === 'pendiente' || cita.estado === 'confirmada';

  return (
    <div className="card flex flex-wrap items-center justify-between gap-3">
      <div className="flex items-center gap-4">
        <span className="w-16 font-display text-lg text-gold-deep">{hora}</span>
        <div>
          <p className="font-medium text-onyx">{cita.cliente_nombre}</p>
          <p className="text-sm text-onyx/60">
            {cita.servicio_nombre} · {cita.profesional_nombre}
          </p>
          {cita.observaciones && <p className="mt-1 text-xs text-onyx/50">{cita.observaciones}</p>}
        </div>
      </div>

      <div className="flex items-center gap-2">
        <span className={`badge ${badge.colorClass}`}>{badge.label}</span>

        {activa && (
          <div className="flex gap-1">
            {staff && cita.estado === 'pendiente' && (
              <button
                className="text-xs text-onyx/60 hover:underline"
                onClick={() => onCambiarEstado(cita, 'confirmada')}
              >
                Confirmar
              </button>
            )}
            <button className="text-xs text-green-700 hover:underline" onClick={() => onCambiarEstado(cita, 'atendida')}>
              Atendida
            </button>
            {staff && (
              <>
                <button
                  className="text-xs text-onyx/60 hover:underline"
                  onClick={() => onCambiarEstado(cita, 'no_asistio')}
                >
                  No asistió
                </button>
                <button className="text-xs text-red-600 hover:underline" onClick={() => onCambiarEstado(cita, 'cancelada')}>
                  Cancelar
                </button>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
